import { useTranslation } from "react-i18next";
import { ArrowRight, Trash2 } from "lucide-react";
import type { SensorDetail } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDateTime } from "../../lib/date.js";
import { sensorLabel } from "./sensor-model.js";

/**
 * The legacy detail panel (ADR-035 §1), with its labels corrected (D5): the legacy printed the `status` column under
 * "Network:" (it is a project namespace) and `updatedAt` as "last login" (it is the last row change). Everything else
 * is the legacy's order: heading · description · Type · ID · the connection block · the manuals using it · Delete.
 */
export function SensorDetailPanel({
  detail,
  onDelete,
  deleteDisabled,
  onOpenManual,
}: {
  detail: SensorDetail;
  onDelete: () => void;
  deleteDisabled?: boolean;
  /**
   * Open one of the manuals that use this sensor (ADR-035 §5). The legacy's list carried a forward arrow linking to
   * `?arc_manual_selected=<id>` — its own comment: "shows manualsselection with sidebar of the manual open" — which
   * is this rewrite's `?arc_facility=<f>&arc_details=<m>` deep link. Absent ⇒ the rows stay plain text.
   */
  onOpenManual?: (facilityId: number, manualId: number) => void;
}) {
  const { t } = useTranslation();
  const { sensor, usage } = detail;
  return (
    <div className="flex max-w-prose flex-col gap-4">
      <div>
        <h2 id="sensor-detail-title" className="m-0 text-lg font-semibold">
          {sensorLabel(sensor)}
        </h2>
        {sensor.description ? <p className="text-muted-foreground m-0 mt-1 text-sm">{sensor.description}</p> : null}
      </div>

      <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t("sensors.type")}</dt>
        <dd className="m-0">{sensor.type ?? "—"}</dd>
        <dt className="text-muted-foreground">{t("sensors.id")}</dt>
        <dd className="m-0 font-medium tabular-nums">{sensor.slug}</dd>
      </dl>

      {sensor.labels.length > 0 ? (
        <div>
          <h3 className="text-muted-foreground m-0 text-xs font-medium uppercase">{t("sensors.switchLabels")}</h3>
          <ul aria-label={t("sensors.switchLabels")} className="m-0 mt-1 flex list-none flex-wrap gap-1.5 p-0">
            {sensor.labels.map((l, i) => (
              <li key={`${l}-${String(i)}`} className="bg-muted text-muted-foreground rounded px-2 py-0.5 text-xs">
                {l}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <h3 className="text-muted-foreground m-0 text-xs font-medium uppercase">{t("sensors.connection")}</h3>
        <dl className="m-0 mt-1 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted-foreground">{t("sensors.group")}</dt>
          <dd className="m-0">{sensor.group ?? "—"}</dd>
          <dt className="text-muted-foreground">{t("sensors.ip")}</dt>
          <dd className="m-0 tabular-nums">{sensor.ip}</dd>
          <dt className="text-muted-foreground">{t("sensors.lastUpdated")}</dt>
          <dd className="m-0">
            <TooltipTrigger delay={0}>
              <span tabIndex={0} className="focus-visible:ring-ring/50 rounded outline-none focus-visible:ring-2">
                {formatDateTime(sensor.updatedAt)}
              </span>
              <Tooltip>{t("sensors.lastUpdatedHint")}</Tooltip>
            </TooltipTrigger>
          </dd>
        </dl>
      </div>

      <div>
        <h3 className="text-muted-foreground m-0 text-xs font-medium uppercase">{t("manuals.raw")}</h3>
        {usage.manuals.length === 0 ? (
          <p className="m-0 mt-1 text-sm">{t("sensors.notUsedInAnyManual")}</p>
        ) : (
          <ul aria-label={t("sensors.manualsUsingThisSensor")} className="m-0 mt-1 flex list-none flex-col gap-1 p-0 text-sm">
            {usage.manuals.map((m) => {
              const label = (
                <>
                  <span className="truncate">{m.name}</span>
                  <span className="text-muted-foreground text-xs">{m.facilityName}</span>
                  {m.state !== "live" ? (
                    <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[0.65rem] font-medium">{m.state === "trashed" ? t("sensors.inTheTrash") : t("sensors.archived")}</span>
                  ) : m.facilityArchived ? (
                    <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[0.65rem] font-medium">{t("sensors.facilityArchived")}</span>
                  ) : null}
                </>
              );
              // Only offer the arrow where it actually LANDS. A trashed manual is not a place to navigate to — the
              // legacy excluded those from this list entirely; we keep showing them because the delete flow names
              // them as blockers, but as plain text. A manual in an ARCHIVED FACILITY is the subtler case (diff
              // panel): archiving a facility never archives the manuals under it, so such a manual still reads
              // `live`, yet the navigator browses the active tree — the deep link would be dropped as stale and
              // dump the caller on the root facility list, having already left this screen.
              const canOpen = onOpenManual != null && m.state !== "trashed" && !m.facilityArchived;
              return (
                <li key={m.id}>
                  {canOpen ? (
                    <button
                      type="button"
                      onClick={() => onOpenManual(m.facilityId, m.id)}
                      aria-label={t("sensors.openNamedInFacility", { name: m.name, facilityName: m.facilityName })}
                      className="hover:bg-muted focus-visible:ring-ring/50 group flex w-full cursor-pointer flex-wrap items-center gap-2 rounded-md border-0 bg-transparent px-2 py-1 text-left text-sm outline-none focus-visible:ring-2"
                    >
                      {label}
                      <ArrowRight aria-hidden="true" className="text-muted-foreground group-hover:text-foreground ml-auto size-4 shrink-0" />
                    </button>
                  ) : (
                    <span className="flex flex-wrap items-center gap-2 px-2 py-1">{label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {usage.elementSettings > 0 ? (
          <p className="text-muted-foreground m-0 mt-1 text-xs">
            {t("sensors.boundToElements", { count: usage.elementSettings })}
          </p>
        ) : null}
      </div>

      <div>
        <Button variant="destructive" size="sm" onPress={onDelete} isDisabled={deleteDisabled}>
          <Trash2 aria-hidden="true" className="size-4" /> {t("sensors.deleteButton")}
        </Button>
      </div>
    </div>
  );
}
